import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { Translate, withTranslate } from 'core/translate';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooterBase,
  ModalFooterMessage,
  ModalFooterButtons,
  ModalFooterCancelButton,
  ModalFooterPrimaryButton,
  ModalCustomContent,
} from '../../components/modal/Modal';
import Currency from '../../common/intl/Currency';
import computePriceChange from './computePriceChange';
import OptionConflictModalContent from './OptionConflictModalContent';
import {
  trackModalLoaded,
  trackModalClosed,
} from '../../factories/analytics/analytics.service';
import { combineClassNames } from '../../../core/css';

const OptionConflictPriceChange = ({
  previousConfig,
  conflictConfig,
  hidePrice,
}) => {
  if (hidePrice) {
    return null;
  }

  const content = conflictConfig.msrp.unknownPriceDisplay ? (
    <span className="option-conflict__price-label__see-dealer">
      {conflictConfig.msrp.unknownPriceDisplay}
    </span>
  ) : (
    <span>
      <Translate value="price.change" />{' '}
      <span className="option-conflict__price">
        <Currency
          value={computePriceChange(previousConfig.msrp, conflictConfig.msrp)}
        />
      </span>
    </span>
  );

  return <div className="option-conflict__price-label">{content}</div>;
};

const OptionConflictModal = (props) => {
  const [selection, setSelection] = useState(null);
  const {
    translate,
    option,
    previousConfig,
    conflictConfig,
    hidePrice,
    onClose,
    modelInfo,
    onResolve,
    basename,
  } = props;

  const location = useLocation();

  useEffect(() => {
    trackModalLoaded('build assistant modal');
    return () => {
      const pathname = location.pathname;
      trackModalClosed(pathname, basename);
    };
  }, []);

  const header = translate('build.assistant');

  const userSelectionOptions = get(
    conflictConfig,
    'config.toggleCheck.userChoiceOptions',
    []
  );
  const requiresUserSelection = userSelectionOptions.length > 0;

  const hasPriceInOptions = (options) =>
    options.find((option) => get(option, 'msrpDisplay.amount.value', 0) > 0);

  if (!requiresUserSelection) {
    const addedOptions = get(conflictConfig, 'config.toggleCheck.added', null);
    const deletedOptions = get(
      conflictConfig,
      'config.toggleCheck.deleted',
      null
    );

    const options = [...addedOptions, ...deletedOptions];
    if (options.length > 0 && !hasPriceInOptions(options)) {
      onResolve(option);
      return null;
    }
  }

  const onSelect = () => {
    const { option, onResolve, onSelect } = props;
    if (requiresUserSelection) {
      onSelect(selection);
    } else {
      onResolve(option);
    }
  };

  const onChange = (selection) => {
    setSelection(selection);
  };

  const FooterButtons = ({ setIsClosing }) => (
    <ModalCustomContent>
      {({ onCloseModal }) => (
        <ModalFooterButtons classModifier="option-conflict">
          <ModalFooterCancelButton
            className="stat-button-link option-conflict-button"
            onClose={() => {
              setIsClosing(true, onCloseModal(onClose));
            }}
            data-dtm="configuration assistant"
          />
          <ModalFooterPrimaryButton
            className="stat-button-link option-conflict-button"
            onSelect={() => {
              setIsClosing(true, onCloseModal(onSelect));
            }}
            disabled={requiresUserSelection && !selection}
            data-dtm="configuration assistant">
            <Translate value="byo.page.general.select" />
          </ModalFooterPrimaryButton>
        </ModalFooterButtons>
      )}
    </ModalCustomContent>
  );

  return (
    <Modal isOpen={true} contentLabel={header} onRequestClose={onClose}>
      <ModalHeader
        icon="bell"
        text={header}
        onClose={onClose}
        analyticsKey="configuration assistant"
      />
      <ModalBody
        className={combineClassNames(
          'option-conflict-modal-body',
          requiresUserSelection ? 'option-conflict-modal-body--user-choice' : ''
        )}
        innerClassName="option-conflict-modal__inner">
        <div className="modal-body__content">
          <OptionConflictModalContent
            option={option}
            conflictConfig={conflictConfig}
            previousConfig={previousConfig}
            hidePrice={hidePrice}
            selection={selection}
            onChange={onChange}
            modelInfo={modelInfo}
            priceContainer={
              <OptionConflictPriceChange
                previousConfig={previousConfig}
                conflictConfig={conflictConfig}
                hidePrice={hidePrice}
              />
            }
          />
        </div>
      </ModalBody>
      {requiresUserSelection ? (
        <ModalFooterBase className="model-footer__option-conflict">
          <ModalFooterMessage>
            {!selection && <Translate value="conflict.make.selection.v3" />}
          </ModalFooterMessage>
          <FooterButtons />
        </ModalFooterBase>
      ) : (
        <ModalFooterBase className="model-footer__option-conflict">
          <OptionConflictPriceChange
            previousConfig={previousConfig}
            conflictConfig={conflictConfig}
            hidePrice={hidePrice}
          />
          <FooterButtons />
        </ModalFooterBase>
      )}
    </Modal>
  );
};

export default withTranslate(OptionConflictModal);
